/**
 * RUN HEALTH — what the Roles index knows about whether a Role's systems are
 * actually WORKING, as one org-scoped read.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * WHY THIS IS NOT A FIELD ON `OrgRolesRollupViewSchema`
 * ══════════════════════════════════════════════════════════════════════════
 *
 * The rollup answers "what has been MODELLED". This answers "what has RUN".
 * They come from different tables, they age at completely different rates, and
 * one of them is a scan over the execution history of every workflow the
 * organisation owns. Folding this into the rollup would put that scan on the
 * critical path of the screen's first paint, for a band of the design that sits
 * below the fold.
 *
 * They are also different KINDS of claim, and that is the load-bearing half.
 * `integrityStatus: DEGRADED` in the rollup is a verdict about the coverage
 * MODEL — somebody's formula could not be evaluated. It is NOT a failing run,
 * it never was, and a card labelled `# ISSUES` fed from it renders a plausible
 * integer that answers a different question with no way for a reader to tell.
 * That substitution was proposed, in writing, and refused. This schema exists so
 * that the honest number has somewhere to come from.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * 🚨 EVERY FIGURE HERE COVERS THE WORKFLOWS A ROLE HOLDS, AND NOTHING ELSE
 * ══════════════════════════════════════════════════════════════════════════
 *
 * A Role can hold six kinds of system (`RoleResourceType`). Exactly ONE of them
 * records whether a run SUCCEEDED, which is the only thing this screen can use:
 *
 * | kind | run-state source |
 * |---|---|
 * | `workflow` | `WorkflowExecution` — status, timestamps, per-workflow. The ONLY kind that records an OUTCOME |
 * | `ai_task` | `SkillExecution` exists and is NOT one. Its single writer is the `ZAiTasks.Test` handler, which hardcodes `isTest: true`; nothing in the product ever writes a production row. Counting it would count how often somebody pressed Test |
 * | `agent` · `deployment` · `external_tool` | ACTIVITY rows exist and carry NO outcome. `Run.agentId`, `TokenUsage.externalToolId` and the 20 models carrying `deploymentId` all have `createdAt` and no status column. A timestamp says something ran, never whether it WORKED, so none of the three can answer this screen's question |
 * | `document_template` | nothing at all. Every `documentTemplateId` carrier is a LINK, never a run: `AgentTask` and `AITask` name the definition a task renders with, and `DocumentTemplateFolderAssignment` files that definition in a folder |
 *
 * So the census is PARTIAL by construction, and it says so in its own payload
 * rather than leaving a reader to assume completeness. `systemsObservable` and
 * `systemsUnobservable` sum to every system the Role holds in `RoleResource`,
 * and a client rendering "3 of 90 systems failing" over an observable
 * population of 4 is publishing a ratio whose denominator it invented.
 *
 * ⚠️ `systemsUnobservable` COUNTS `RoleResource` ONLY. A Role also holds
 * Collections and Workspaces through `RoleCollectionGrant` and
 * `RoleWorkspaceGrant`, which is why the index's own `systems` figure comes
 * from a THREE-table count. Neither grant kind can run, so neither belongs in a
 * run census — but it does mean `systemsObservable + systemsUnobservable` is
 * not the index's `systems`, and treating the two as the same number is the
 * "two screens, two populations" defect one read over.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * 🚨 A RUN IS A REAL TOP-LEVEL RUN, AND THE NAIVE COUNT IS ~6x TOO BIG
 * ══════════════════════════════════════════════════════════════════════════
 *
 * A loop or do-while node writes one `WorkflowExecution` row PER BODY PASS, and
 * the builder writes one per single-node test. Counting the table without
 * excluding both over-counted the reported workflow six-fold: 554 of 660 rows
 * were body passes (NEX-3178). Every other reader of these rows already pins
 * `parentNodeId: null` + `wasTestExecution: false` — the product's own execution
 * history, the v1 list, and the workflow-introspection node. This census pins
 * them too, so the number on the Roles index cannot disagree with the number on
 * the workflow's own history page.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * THE WINDOW IS SERVED, NEVER ASSUMED
 * ══════════════════════════════════════════════════════════════════════════
 *
 * "Failing" is meaningless without a period, and a client that picks its own
 * would label a server figure with a window the server did not use. So the
 * window travels with the payload. Widening it is a contract change and a
 * deliberate one — every figure below moves when it changes.
 */
import { z } from "zod";

/**
 * How many real top-level runs the window holds, by outcome.
 *
 * The five `WorkflowExecutionStatus` members are folded into four buckets
 * because `PENDING` and `RUNNING` are the same thing to a reader — work that
 * has not finished yet — and splitting them invites a card that treats a queued
 * run as a stalled one. Nothing here is nullable: a Role with no runs reports
 * four zeros, and those zeros are MEASURED. The absence case is
 * `systemsObservable: 0`, which is a different statement and has its own field.
 */
export const ROLE_RUN_OUTCOMES = ["succeeded", "failed", "cancelled", "inFlight"] as const;

/**
 * The four buckets, as a value the server can iterate.
 *
 * Exported because the read port, its adapter and the fold must all agree on
 * this vocabulary, and the only way to make a fifth bucket a compile error in
 * all three at once is for all three to name the same union. The adapter maps
 * the database's own status enum onto it, which is where the translation
 * belongs — nothing above the adapter should ever see a `WorkflowExecutionStatus`.
 */
export type RoleRunOutcome = (typeof ROLE_RUN_OUTCOMES)[number];

export const RoleRunOutcomeCountsSchema = z.strictObject({
  /** `COMPLETED`. */
  succeeded: z.number().int().min(0),
  /** `FAILED`. This is the number an `# ISSUES` card is asking for. */
  failed: z.number().int().min(0),
  /** `CANCELLED` — stopped on purpose, so never counted as a failure. */
  cancelled: z.number().int().min(0),
  /** `PENDING` + `RUNNING`. Not yet an outcome, and never an error. */
  inFlight: z.number().int().min(0)
});

export type RoleRunOutcomeCounts = z.infer<typeof RoleRunOutcomeCountsSchema>;

/**
 * How one observable system is doing, as a closed vocabulary.
 *
 * The same three words the per-Role counts are named after, so a client can
 * hold ONE map from state to colour instead of one per surface. A fourth member
 * is a compile error in the fold that emits it and in every exhaustive map over
 * it, which is the only reason this is a union rather than a boolean pair.
 *
 * IDLE IS NOT HEALTHY AND IT IS NOT BROKEN — the reason is on
 * {@link RoleRunHealthRowSchema} and is not repeated here.
 */
export const ROLE_SYSTEM_RUN_STATES = ["failing", "idle", "healthy"] as const;

export type RoleSystemRunState = (typeof ROLE_SYSTEM_RUN_STATES)[number];

/**
 * One observable system, named and classified.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * 🚨 AN ID, AND NO NAME. THAT IS A CONTRACT LIMIT, NOT AN OVERSIGHT.
 * ══════════════════════════════════════════════════════════════════════════
 *
 * `systemId` is the `RoleResource.resourceId` of a system whose kind has a run
 * history — a `Workflow.id` today, and the same id the workflow's own execution
 * history filters on. It is enough to ADDRESS a system: to key a mark, to link
 * to its runs, to tell two failing systems apart.
 *
 * It is NOT enough to LABEL one. This read costs three queries and none of them
 * touches `Workflow`, so serving a name here would be a fourth read on the
 * critical path of a band that sits below the fold — the exact cost this
 * schema's own header refuses to put on the rollup. A consumer that needs a
 * human label has to get it from a read that already holds one, and a control
 * built on an id alone must say what it acts on some other way or stay a
 * picture.
 */
export const RoleObservedSystemSchema = z.strictObject({
  /** `RoleResource.resourceId` — a `Workflow.id` today. Unique within the org. */
  systemId: z.string(),
  state: z.enum(ROLE_SYSTEM_RUN_STATES)
});

export type RoleObservedSystem = z.infer<typeof RoleObservedSystemSchema>;

/**
 * What a run-count delta is allowed to SAY, as a closed vocabulary.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * 🚨 A PERCENTAGE CHANGE IS A LIE IN FOUR OF THESE FIVE STATES
 * ══════════════════════════════════════════════════════════════════════════
 *
 * `(current − prior) / prior` is arithmetic that renders for any pair of
 * integers and MEANS something for almost none of them. Every arm below is a
 * pair the formula would happily turn into a confident percentage, and the
 * whole point of this union is that four of them cannot reach one — the state
 * is decided BEFORE the division, so an unmeaningful delta is unrepresentable
 * rather than merely discouraged.
 *
 * | state | the pair that produces it | why no percentage |
 * |---|---|---|
 * | `comparable` | prior ≥ {@link ROLE_RUN_MOMENTUM_MIN_PRIOR_RUNS} | — it is served |
 * | `too-few-runs` | `0 < prior < FLOOR` | one run moves the figure further than the smallest move a reader can act on |
 * | `no-prior-runs` | `prior === 0`, `current > 0` | division by zero. `+100%` is not a smaller lie than `∞`, it is a more believable one |
 * | `no-runs` | both zero, Role older than the prior window | nothing ran in either window; a delta of nothing is not `0%` growth |
 * | `role-too-new` | `prior === 0` AND the Role was created after the prior window opened | the empty prior window is a statement about the calendar rather than about the Role |
 *
 * 🚨 A YOUNG ROLE DOES NOT SUPPRESS A REAL DELTA, AND THE OPPOSITE RULE WAS
 * TRIED AND FALSIFIED ON STAGING. `role-too-new` is consulted ONLY where the
 * prior window is EMPTY. Runs attach to a WORKFLOW, never to a Role, so a Role
 * created days ago that is given an established workflow inherits that
 * workflow's real history immediately — measured on staging, one such Role read
 * 168 runs in EACH window. Withholding that checkable 0% because the container
 * is young would hide a true fact behind a sentence claiming there is nothing to
 * compare.
 *
 * The age cannot invalidate the ratio because it applies to BOTH windows
 * equally: they cover the same workflows over two equal, adjacent spans, so
 * whatever the Role's age does to one it does to the other and it cancels. What
 * the age genuinely explains is an EMPTY prior window — which is why it now
 * discriminates between the two zero-prior arms rather than vetoing a
 * non-empty one. "This Role is new" and "this Role stopped running" are
 * opposite instructions to an owner, and only the age separates them.
 *
 * 🚨 `+100%` FOR A ZERO PRIOR IS THE HOUSE'S OWN EXISTING SPELLING AND IT IS
 * REFUSED HERE. `PercentageChange.calculate` in `src/dashboard/` returns
 * exactly that, and it is the reason this module does its own arithmetic rather
 * than importing it: a Role that ran nothing last week and twice this week is
 * not growing at the same rate as one that went 50 → 100, and a reader has no
 * way to tell those two `+100%` apart. That VO is correct for the dashboard's
 * own tiles and wrong for this figure; the difference is the honesty rule, not
 * the formula.
 *
 * ⚠️ ANNUALISING ANY OF THIS IS FORBIDDEN. The window is
 * {@link ROLE_RUN_HEALTH_WINDOW_DAYS} days and run volume is bursty — a
 * deployment, a backfill or one busy Tuesday moves a week by a multiple.
 * Multiplying by 52 turns that burst into a yearly claim nothing supports.
 */
export const ROLE_RUN_MOMENTUM_STATES = [
  "comparable",
  "too-few-runs",
  "no-prior-runs",
  "no-runs",
  "role-too-new"
] as const;

export type RoleRunMomentumState = (typeof ROLE_RUN_MOMENTUM_STATES)[number];

/**
 * The smallest prior-window run count a percentage may be divided by.
 *
 * 🚨 THIS IS A RESOLUTION FLOOR, NOT A TASTE. Below it, ONE run moves the
 * reported figure by more than the smallest move the design draws: at a prior
 * of 10 a single run is 10 points, and the register's own reference chip shows
 * `↑4%` as a meaningful reading. So under 10, the percentage's own granularity
 * is coarser than the distinctions it is being asked to express, and two Roles
 * that differ by one run render as a 100-point gap.
 *
 * Reporting the two COUNTS instead is strictly more informative there: `3 runs,
 * against 2` is checkable, bounded and cannot be misread as a rate.
 *
 * The floor is on the PRIOR window alone, because the prior window is the
 * DENOMINATOR — it is the only one whose smallness makes the ratio unstable. A
 * current window of 500 against a prior of 1 is `+49,900%`, which is arithmetic
 * nobody can act on; the honest sentence is still "1 run, then 500".
 */
export const ROLE_RUN_MOMENTUM_MIN_PRIOR_RUNS = 10;

/**
 * One Role's run momentum — this window's runs against the window before it.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * THE TWO WINDOWS ARE ADJACENT AND EXACTLY EQUAL IN LENGTH
 * ══════════════════════════════════════════════════════════════════════════
 *
 * With {@link ROLE_RUN_HEALTH_WINDOW_DAYS} at 7, `currentRuns` covers days 1–7
 * and `priorRuns` covers days 8–14, counted back from the instant the read ran.
 * They share a boundary and neither overlaps the other.
 *
 * 🚨 EQUAL LENGTH IS LOAD-BEARING, WHICH IS WHY THE CURRENT WINDOW CARRIES AN
 * UPPER BOUND IT DOES NOT STRICTLY NEED. An unbounded current window is longer
 * than the prior one by however long the request takes, and a delta between two
 * windows of different lengths is biased UP by construction — small, systematic,
 * in the flattering direction, and invisible in the output.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * A RUN IS A RUN THAT STARTED, WHATEVER IT WENT ON TO DO
 * ══════════════════════════════════════════════════════════════════════════
 *
 * Both counts sum all four {@link ROLE_RUN_OUTCOMES} buckets, and that is a
 * BIAS DECISION rather than a convenience. Counting only `succeeded` would
 * compare a current window full of `inFlight` runs — work that started and has
 * not finished — against a prior window where a week has passed and almost
 * everything is terminal. Every Role would then read as slowing down, and the
 * effect grows with how busy the Role is. The window predicate is `createdAt`,
 * so "runs that started in the window" is the quantity the query actually
 * measures, and it is symmetric across both windows.
 *
 * This is MOMENTUM, never health: a Role whose runs all fail can have excellent
 * momentum. `systemsFailing` on the same row is the health figure, and the two
 * must never be blended into one verdict.
 */
export const RoleRunMomentumSchema = z.strictObject({
  state: z.enum(ROLE_RUN_MOMENTUM_STATES),
  /** Runs that STARTED in the current window, all outcomes. */
  currentRuns: z.number().int().min(0),
  /** Runs that STARTED in the window immediately before it, all outcomes. */
  priorRuns: z.number().int().min(0),
  /**
   * The change from `priorRuns` to `currentRuns`, in PERCENTAGE POINTS — `34`
   * means `+34%`, `-18` means a fall of 18%. Unbounded above.
   *
   * 🚨 NOT A 0–1 RATIO, unlike `RoleCoverageView`'s automation figure, and not
   * formattable by the same helper. A change can exceed 100% and is signed;
   * a ratio can do neither.
   *
   * 🚨 NON-NULL EXACTLY WHEN `state === "comparable"`, in both directions. That
   * biconditional is asserted by the fold's own spec, so a client may branch on
   * either one and can never meet a state that carries a number it must not
   * draw, nor a `comparable` row with nothing to draw. A client that reads this
   * field without reading `state` gets `null` rather than a plausible zero.
   */
  changePercent: z.number().nullable()
});

export type RoleRunMomentum = z.infer<typeof RoleRunMomentumSchema>;

/**
 * One Role's run census.
 *
 * 🚨 THE THREE SYSTEM BUCKETS PARTITION `systemsObservable` EXACTLY, and that
 * is an invariant rather than a coincidence: `systemsFailing + systemsHealthy +
 * systemsIdle === systemsObservable`, always. A client may therefore draw the
 * tick strip straight from these three without a fourth "unknown" bucket, and a
 * fold that loses a system fails its own test instead of quietly shrinking a
 * denominator.
 *
 * `observedSystems` says WHICH systems those counts are about, one entry per
 * observable system, and reconciles with all four fields exactly — see its own
 * note. It costs no read: the fold already holds every `RoleResource.resourceId`
 * it needs to look a workflow's runs up, and used to discard them.
 *
 * IDLE IS NOT HEALTHY AND IT IS NOT BROKEN. A workflow nobody has run this week
 * is the most common state on a real tenant, and colouring it green claims a
 * success that never happened while colouring it red invents an incident. It is
 * its own bucket for that reason, and the design already has a token for it.
 */
export const RoleRunHealthRowSchema = z.strictObject({
  roleId: z.string(),
  /**
   * Systems held whose kind has a run history — workflows, today.
   * `0` means this Role holds nothing that can be observed, which is why no
   * ratio below it may be rendered.
   */
  systemsObservable: z.number().int().min(0),
  /**
   * Systems held in `RoleResource` whose kind has NO run history at all.
   * Not a failure and not a gap in anyone's configuration — an agent simply has
   * no execution table. Served so a client can say what its census excludes.
   */
  systemsUnobservable: z.number().int().min(0),
  /** Observable systems with at least one FAILED run in the window. */
  systemsFailing: z.number().int().min(0),
  /** Observable systems that ran in the window and never failed. */
  systemsHealthy: z.number().int().min(0),
  /** Observable systems with no real top-level run in the window at all. */
  systemsIdle: z.number().int().min(0),
  /**
   * WHICH systems those three counts are about, one entry each.
   *
   * ⚠️ THIS AND THE THREE COUNTS ABOVE ARE ONE MEASUREMENT SERVED TWICE, and
   * the reconciliation is exact rather than approximate:
   * `observedSystems.length === systemsObservable`, and the histogram of
   * `state` over this array equals `systemsFailing` / `systemsIdle` /
   * `systemsHealthy` term for term. `rollUpRoleRunHealth` emits both from the
   * SAME per-workflow verdict in one pass, so they cannot be derived
   * differently, and its spec asserts the reconciliation rather than trusting
   * it. A client should read ONE of the two and never mix them — a picture
   * drawn from these entries under a caption divided by those counts is the
   * disagreement the whole pair exists to make unrepresentable.
   *
   * 🚨 THE ORDER IS NOT MEANINGFUL. Entries arrive in the order the
   * organisation's `RoleResource` rows were read, which is a property of the
   * query plan and of nothing a reader cares about. Anomaly-first is a
   * PRESENTATION rule and belongs to whoever draws the marks; a server that
   * sorted here would make two clients disagree about whose rule it is.
   *
   * An empty array means this Role holds nothing that can report a run, which
   * is the same statement as `systemsObservable: 0` and is the common case.
   */
  observedSystems: z.array(RoleObservedSystemSchema),
  /** Real top-level runs in the window, over the observable systems. */
  runs: RoleRunOutcomeCountsSchema,
  /**
   * This window's run volume against the window before it.
   *
   * ⚠️ `momentum.currentRuns` IS THE SUM OF `runs` ABOVE, and that is an
   * invariant the fold's spec asserts rather than a coincidence — both are
   * written from the same per-workflow counts in the same pass. It is served
   * flat anyway so the delta and its own numerator cannot be assembled from two
   * places by a client, which is the same rule `observedSystems` keeps against
   * the three counters beside it.
   *
   * There is deliberately NO momentum on `OrgRolesRunHealthTotals`. Every other
   * field there is a plain sum of its per-Role twin, and momentum is not
   * summable: `role-too-new` is a statement about ONE Role's age with no
   * organisation-level meaning, so an org band would need a second, different
   * state machine. One vocabulary, one place.
   */
  momentum: RoleRunMomentumSchema
});

export type RoleRunHealthRow = z.infer<typeof RoleRunHealthRowSchema>;

/**
 * The org-wide band, over the same window and the same definition of a run.
 *
 * `rolesTotal` is here and `rolesObserved` beside it for the reason every other
 * total in this slice carries a census: an organisation where two Roles of
 * eleven hold a workflow has a run figure that is TRUE about those two and says
 * nothing about the other nine. Publishing the bare integer makes it read as a
 * statement about the organisation.
 */
export const OrgRolesRunHealthTotalsSchema = z.strictObject({
  /** Every Role in the organisation. */
  rolesTotal: z.number().int().min(0),
  /** Roles holding at least one observable system — the population summed below. */
  rolesObserved: z.number().int().min(0),
  /** Observable systems across every Role. */
  systemsObservable: z.number().int().min(0),
  /** Held systems with no run history, across every Role. */
  systemsUnobservable: z.number().int().min(0),
  /** Observable systems with at least one failure in the window. */
  systemsFailing: z.number().int().min(0),
  /** Observable systems that ran and never failed. */
  systemsHealthy: z.number().int().min(0),
  /** Observable systems with no run in the window. */
  systemsIdle: z.number().int().min(0),
  /** Every real top-level run in the window, by outcome. */
  runs: RoleRunOutcomeCountsSchema
});

export type OrgRolesRunHealthTotals = z.infer<typeof OrgRolesRunHealthTotalsSchema>;

/**
 * The run-health read, as one org-scoped payload.
 *
 * ⚠️ NOTHING HERE IS PER-VIEWER, and nothing here may become per-viewer. The
 * sibling `RoleCoverageViewSchema` is re-exported verbatim as a public-API
 * response, and the reflex that puts a caller-dependent field on a view schema
 * is what makes such a field permanently false for an API key. This schema is
 * not exposed on `/api/public/v1` today; keep it that way by keeping it
 * viewer-independent, so exposing it later is a routing decision and not a
 * redesign.
 */
export const OrgRolesRunHealthViewSchema = z.strictObject({
  organizationId: z.string(),
  /**
   * How many days back every figure above covers, counted from the moment the
   * read ran. Served rather than assumed — see the header.
   */
  windowDays: z.number().int().min(1),
  roles: z.array(RoleRunHealthRowSchema),
  totals: OrgRolesRunHealthTotalsSchema
});

export type OrgRolesRunHealthView = z.infer<typeof OrgRolesRunHealthViewSchema>;

/**
 * The window every figure in this view covers.
 *
 * Seven days, matching the design's own `runs last week`. It is a CONSTANT
 * rather than a query parameter on purpose: a caller-chosen window is a second
 * cache key, a second denominator and a second way for two panels on one screen
 * to disagree about what "failing" means.
 */
export const ROLE_RUN_HEALTH_WINDOW_DAYS = 7;
